import { FC } from 'react';
import { withStyles, Table, Icon, Box, Button, Tooltip, Domain, Badge } from 'vopay';
import type { DomainStatus, BadgeProps } from 'vopay';
import Layout from '../Layout';
import styles from './ViewDomain.module.scss';
import { useConfirmDialog } from '../../../../providers/ConfirmDialog.provider';
import { useToast } from '../../../../providers/Toast.provider';

const classes = withStyles(styles);

type ViewDomainProps = {
  domain: Domain;
  onDelete: () => void;
  onVerify: () => void;
};

const ViewDomain: FC<ViewDomainProps> = ({
  domain: { Domain: url, Registrar, CnameRecords, Status },
  onDelete,
  onVerify,
}) => {
  const confirmDialog = useConfirmDialog();
  const toast = useToast();

  function copy(value: string) {
    navigator.clipboard.writeText(value);

    toast.success(`Copied ${value} to clipboard`);
  }

  const handleDeleteDomain = async () => {
    const confirmation = await confirmDialog.open('Delete Domain', {
      subTitle: `Are you sure you want to delete ${url}?`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    });

    if (confirmation !== 'confirm') return;

    onDelete();
  };

  return (
    <Layout
      explanation={
        <>
          <b>Configure your DNS records for {url}</b>

          <p>
            You’ll need to edit the DNS settings of your domain name.{' '}
            <a
              href="https://support.smtp2go.com/hc/en-gb/sections/360004372633-DNS-Setup?_ga=2.161647669.86896589.1655129047-1970868669.1616779892"
              className={classes('guidesLink')}
            >
              We have some guides for popular webhosts here.
            </a>{' '}
            Note that it can take up to an hour for some providers to make DNS changes live (it normally only takes a
            few mintues though).
          </p>

          <p>
            <b>Step 1</b> Go to your DNS provider {Registrar !== 'Unknown' && <b>({Registrar})</b>}
          </p>

          <p>
            <b>Step 2</b> Add the following CNAME records:
          </p>
        </>
      }
      content={
        <>
          <div className={classes('status')}>
            Status: <Chip status={Status} />
          </div>

          <Table
            version="2"
            rows={Object.values(CnameRecords).map((r) => ({
              id: r.Hostname,
              data: r,
            }))}
            columns={[
              {
                id: 'type',
                header: 'Type',
                template: (r) => (
                  <div className={classes('cnameCell')}>
                    <div className={classes('cnameBadge', r.data.Verified && 'verified')}>CNAME</div>

                    {r.data.Verified && <Icon icon="check2" className={classes('verifiedIcon')} />}

                    {!r.data.Verified && (
                      <Tooltip
                        icon="info"
                        information={`Lookup CNAME(${r.data.Hostname}) on clint.ns.cloudfare.com returned no target`}
                        position="topRight"
                        bgColor="var(--error)"
                        color="red"
                      />
                    )}
                  </div>
                ),
                align: 'left',
              },
              {
                id: 'hostName',
                header: 'Host Name',
                template: (r) => (
                  <div>
                    <div className={classes('cellTop')}>
                      <div>{r.data.Hostname}</div>

                      <Icon icon="copy" width="20px" height="20px" onClick={() => copy(r.data.Hostname)} />
                    </div>
                    <p className={classes('cellBottom')}>{r.data.Text}</p>
                  </div>
                ),
                align: 'left',
              },
              {
                id: 'instructions',
                header: 'Enter This Value',
                template: (r) => (
                  <div>
                    <div className={classes('cellTop')}>
                      <div>{r.data.Target}</div>

                      <Icon icon="copy" width="20px" height="20px" onClick={() => copy('return.smtp2go.net')} />
                    </div>

                    {!r.data.Verified && <p className={classes('cellBottom')}>Current value: No Record Found</p>}
                  </div>
                ),
                align: 'left',
              },
            ]}
          />

          <Box ai="center" gap="sm">
            <Button label="Delete" color="red" onClick={handleDeleteDomain} />

            <Button
              label="Verify"
              isDisabled={Status === 'verified'}
              className={classes('verifyButton')}
              onClick={onVerify}
            />
          </Box>
        </>
      }
    />
  );
};

export default ViewDomain;

function Chip({ status }: { status: DomainStatus }) {
  const labelMap: Record<DomainStatus, string> = {
    verified: 'Verified',
    unverified: 'Unverified',
  };

  const colorMap: Record<DomainStatus, BadgeProps['color']> = {
    verified: 'positive',
    unverified: 'negative',
  };

  return <Badge label={labelMap[status]} color={colorMap[status]} hasIcon />;
}
